import * as THREE from 'three';
import { edgeLines, markOverlay, setEdgeStyle } from './edges';
import type { EdgeLines } from './edges';

const MARKER_VERTEX = /* glsl */ `
  #include <clipping_planes_pars_vertex>
  uniform float uSize;
  void main() {
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = uSize;
    gl_Position = projectionMatrix * mvPosition;
    #include <clipping_planes_vertex>
  }
`;

const MARKER_FRAGMENT = /* glsl */ `
  #include <clipping_planes_pars_fragment>
  uniform vec3 uColor;
  void main() {
    #include <clipping_planes_fragment>
    float d = length(gl_PointCoord - 0.5);
    if (d > 0.5) discard;
    gl_FragColor = vec4(mix(uColor, vec3(1.0), smoothstep(0.32, 0.38, d)), 1.0);
  }
`;

export type MarkerPoints = THREE.Points<THREE.BufferGeometry, THREE.ShaderMaterial>;

/** Round markers drawn over everything, `capacity` of them at most. */
export function markerPoints(color: number, sizePx: number, capacity: number): MarkerPoints {
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Color().setHex(color, THREE.LinearSRGBColorSpace) },
      uSize: { value: sizePx },
    },
    vertexShader: MARKER_VERTEX,
    fragmentShader: MARKER_FRAGMENT,
    depthTest: false,
    transparent: true,
    clipping: true,
  });
  const geometry = new THREE.BufferGeometry().setAttribute(
    'position',
    new THREE.Float32BufferAttribute(new Float32Array(capacity * 3), 3),
  );
  geometry.setDrawRange(0, 0);
  const points = markOverlay(new THREE.Points(geometry, material));
  points.frustumCulled = false;
  points.renderOrder = 3;
  points.onBeforeRender = (renderer) => {
    material.uniforms.uSize.value = sizePx * renderer.getPixelRatio();
  };
  return points;
}

export function showPoints(points: THREE.Points, at: readonly THREE.Vector3[]): void {
  const position = points.geometry.getAttribute('position') as THREE.BufferAttribute;
  at.forEach((point, i) => position.setXYZ(i, point.x, point.y, point.z));
  position.needsUpdate = true;
  points.geometry.setDrawRange(0, at.length);
}

export function disposePoints(points: MarkerPoints): void {
  points.removeFromParent();
  points.geometry.dispose();
  points.material.dispose();
}

export function highlightLines(
  edge: Float32Array,
  color: number,
  widthPx: number,
  clip: THREE.Plane | null,
): EdgeLines {
  const geometry = new THREE.BufferGeometry().setAttribute(
    'position',
    new THREE.Float32BufferAttribute(edge, 3),
  );
  const lines = edgeLines(geometry, widthPx);
  setEdgeStyle(lines, color, 1);
  lines.material.clippingPlanes = clip ? [clip] : null;
  lines.renderOrder = 2;
  return lines;
}

export function disposeLines(lines: EdgeLines | null): void {
  if (!lines) return;
  lines.removeFromParent();
  lines.geometry.dispose();
  lines.material.dispose();
}
